import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import { useToast } from "../components/ToastProvider";
import { apiRequest } from "../api/client";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchActowizFeatures,
  fetchActowizProducts,
  fetchActowizTopDeals,
} from "../store/actowiz/actowizSlice";
import Rectangle1 from "../assets/Rectangle1.svg";
import Rectangle2 from "../assets/Rectangle2.svg";
import Rectangle3 from "../assets/Rectangle3.svg";
import Rectangle4 from "../assets/Rectangle4.svg";

const parseMoney = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replaceAll(",", ".").replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRatingValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseFloat(String(value).trim().replace(",", ".").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseReviewCountValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const matchesId = (p, matchId) => {
  if (!p || matchId == null) return false;
  const candidateIds = [
    p?.pid,
    p?.product_id,
    p?.sku,
    p?.id,
    p?.productId,
    p?.productID,
  ]
    .map((v) => (v == null ? "" : String(v)))
    .filter(Boolean);
  return candidateIds.includes(String(matchId));
};

const normalizePlatformKey = (platform) => {
  const p = String(platform || "").trim().toLowerCase();
  // Actowiz / providers may return variants like "ASDA Groceries", "Sainsbury’s", etc.
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("morris")) return "morrison";
  if (p.includes("asda")) return "asda";
  if (p.includes("sains")) return "sainsbury";
  if (p === "sainsburry") return "sainsbury";
  return "";
};

const getProductImages = (prod) => {
  if (!prod) return [];
  if (Array.isArray(prod.images) && prod.images.length) {
    return prod.images.filter(Boolean);
  }
  return [prod.image, prod.image_url, prod.imageUrl, prod.thumbnail, prod.thumb].filter(Boolean);
};

const pickVisitUrl = (obj) =>
  obj?.url ||
  obj?.product_url ||
  obj?.productUrl ||
  obj?.store_url ||
  obj?.storeUrl ||
  obj?.visit_url ||
  obj?.visitUrl ||
  obj?.deep_link ||
  obj?.deepLink ||
  obj?.link ||
  null;

// Same retailer banners as home page `SupermarketTabs` (Rectangle1–4).
const retailerBannerByPlatform = {
  sainsbury: Rectangle1,
  tesco: Rectangle2,
  asda: Rectangle3,
  morrison: Rectangle4,
};

// Note: `actowiz/search` helpers were removed because this page now reuses
// `product.comparison` from already-fetched product list responses.

function ProductDetail({ onOpenAuth, onOpenMenu }) {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const [selectedSize, setSelectedSize] = useState("");
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const features = useAppSelector((s) => s.actowiz.features);
  const [isHydrating, setIsHydrating] = useState(false);
  const [cachedProduct, setCachedProduct] = useState(null);
  const [addToListBusy, setAddToListBusy] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const { showToast } = useToast();

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    if (!id) {
      setCachedProduct(null);
      return;
    }
    try {
      const raw = window.sessionStorage.getItem(`product-detail-cache:${id}`);
      if (!raw) {
        setCachedProduct(null);
        return;
      }
      const parsed = JSON.parse(raw);
      setCachedProduct(parsed && typeof parsed === "object" ? parsed : null);
    } catch {
      setCachedProduct(null);
    }
  }, [id]);

  const product = useMemo(() => {
    if (!id) return null;
    const actowizAll = [
      ...(Array.isArray(topDeals) ? topDeals : []),
      ...(Array.isArray(features) ? features : []),
      ...(byPlatform?.tesco || []),
      ...(byPlatform?.morrison || []),
      ...(byPlatform?.asda || []),
      ...(byPlatform?.sainsbury || []),
    ];
    const fromActowiz =
      actowizAll.find((p) => matchesId(p, id)) ||
      actowizAll.find((p) => p?.sku != null && String(p.sku) === String(id));
    if (fromActowiz) return fromActowiz;
    if (cachedProduct && matchesId(cachedProduct, id)) return cachedProduct;
    return null;
  }, [id, byPlatform, topDeals, features, cachedProduct]);

  useEffect(() => {
    if (!id || !product) return;
    try {
      window.sessionStorage.setItem(`product-detail-cache:${id}`, JSON.stringify(product));
    } catch {
      // Ignore cache write failures.
    }
  }, [id, product]);

  useEffect(() => {
    if (!id || product || isHydrating) return;
    setIsHydrating(true);
    const run = async () => {
      try {
        await Promise.all([
          dispatch(fetchActowizTopDeals({ start: 0, offset: 60, mode: "replace", force: true })),
          dispatch(fetchActowizFeatures({ start: 0, offset: 60, mode: "replace", force: true })),
          dispatch(
            fetchActowizProducts({
              platform: "tesco",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "morrison",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "asda",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "sainsbury",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
        ]);
      } finally {
        setIsHydrating(false);
      }
    };
    run();
  }, [id, product, isHydrating, dispatch]);

  async function handleAddToList(row) {
    if (!product?.pid && !product?.id && !id) {
      showToast({
        type: "error",
        title: "Add failed",
        message: "Product id not found for this item.",
      });
      return;
    }

    try {
      setAddToListBusy(true);

      // Required: fetch add-to-list dropdown data from minimal lists API.
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(
          errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
        );
      }

      const minimalLists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];
      if (!minimalLists.length) {
        showToast({
          type: "error",
          title: "No grocery list",
          message: "No grocery list found. Please create a list first.",
        });
        return;
      }

      let selectedListId = null;
      if (minimalLists.length === 1) {
        selectedListId =
          minimalLists[0]?.id ?? minimalLists[0]?.grocery_list_id ?? minimalLists[0]?.list_id;
      } else {
        const selected = await chooseList(minimalLists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id;
      }
      if (!selectedListId) {
        showToast({
          type: "error",
          title: "Add failed",
          message: "Unable to select grocery list.",
        });
        return;
      }

      const productId = product?.pid ?? product?.product_id ?? product?.id ?? id;
      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
          // `row.platform` is expected from the "Where To Buy" rows; fall back to product fields if needed.
          platform:
            normalizePlatformKey(
              row?.platform ||
                product?.platform ||
                product?.retailer ||
                product?.provider ||
                product?.store ||
                product?.site
            ) || undefined,
          image_url: product?.image || product?.image_url || product?.thumbnail || "",
          price: typeof row?.price === "number" ? row.price : null,
          weight: selectedSize || product?.weight || "",
          quantity: 1,
        },
      });

      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      showToast({
        type: "success",
        title: "Added to list",
        message: "Product added to grocery list.",
        actionLabel: "Go to Grocery List",
        onAction: () => {
          window.location.assign("/settings?tab=grocery&view=lists");
        },
      });
    } catch (err) {
      const errorMessage = err?.message || "Failed to add product to list.";
      if (errorMessage === "ADD_TO_LIST_CANCELLED" || err?.code === "ADD_TO_LIST_CANCELLED") {
        return;
      }
      showToast({
        type: "error",
        title: "Add failed",
        message: errorMessage,
      });
    } finally {
      setAddToListBusy(false);
    }
  }

  // Reuse comparison data that already comes with the product lists
  // (features/top-deals endpoints attach `comparison` per item).
  // This avoids calling `api/actowiz/search` on every product detail view.
  const comparisonByPlatform = useMemo(() => {
    if (!product?.comparison) return null;

    const base = { tesco: null, morrison: null, asda: null, sainsbury: null };
    const comp = product.comparison;

    // Expected shape from this codebase: [{ platform, price }, ...]
    if (Array.isArray(comp)) {
      comp.forEach((x) => {
        const key = normalizePlatformKey(
          x?.platform || x?.retailer || x?.provider || x?.store || x?.site
        );
        if (!key) return;
        const url = pickVisitUrl(x);
        const price = parseMoney(
          x?.price ?? x?.unit_price ?? x?.price_per_unit ?? x?.offer_price ?? x?.offerPrice
        );
        const rating = parseRatingValue(
          x?.average_rating ?? x?.avg_rating ?? x?.rating ?? x?.review_rating
        );
        const reviewCount = parseReviewCountValue(
          x?.review_count ?? x?.reviews ?? x?.rating_count ?? x?.reviewCount
        );
        const prev = base[key];
        if (Number.isFinite(price)) {
          if (prev == null || !Number.isFinite(prev?.price) || price < prev.price) {
            base[key] = { price, url, rating, reviewCount };
          } else if (!prev?.url && url) {
            base[key] = { ...prev, url };
          } else if (prev?.rating == null && rating != null) {
            base[key] = { ...prev, rating };
          } else if (prev?.reviewCount == null && reviewCount != null) {
            base[key] = { ...prev, reviewCount };
          }
        } else if (url && prev == null) {
          base[key] = { price: null, url, rating, reviewCount };
        }
      });
      return base;
    }

    // If API ever returns an object map already.
    if (typeof comp === "object" && comp) {
      (Object.keys(base) || []).forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(comp, k)) {
          const value = comp[k];
          const rawPrice =
            value && typeof value === "object"
              ? value?.price ?? value?.unit_price ?? value?.price_per_unit ?? value?.offer_price
              : value;
          const price = parseMoney(rawPrice);
          const url = value && typeof value === "object" ? pickVisitUrl(value) : null;
          const rating =
            value && typeof value === "object"
              ? parseRatingValue(
                  value?.average_rating ?? value?.avg_rating ?? value?.rating ?? value?.review_rating
                )
              : null;
          const reviewCount =
            value && typeof value === "object"
              ? parseReviewCountValue(
                  value?.review_count ?? value?.reviews ?? value?.rating_count ?? value?.reviewCount
                )
              : null;
          if (Number.isFinite(price) || url) {
            base[k] = { price: Number.isFinite(price) ? price : null, url, rating, reviewCount };
          }
        }
      });
      return base;
    }

    return null;
  }, [product]);

  const retailerStack = useMemo(() => {
    const platforms = ["tesco", "morrison", "asda", "sainsbury"];
    const labelByPlatform = {
      tesco: { label: "Tesco", color: "#1359EF" },
      morrison: { label: "Morrisons", color: "#A20886" },
      asda: { label: "Asda", color: "#08A237" },
      sainsbury: { label: "Sainsbury's", color: "#EC570D" },
    };

    const normalizeIdentityText = (value) => String(value || "").trim().toLowerCase();
    const currentSku = normalizeIdentityText(product?.sku);
    const currentTitle = normalizeIdentityText(product?.title);
    const allKnownProducts = [
      ...(Array.isArray(topDeals) ? topDeals : []),
      ...(Array.isArray(features) ? features : []),
      ...(byPlatform?.tesco || []),
      ...(byPlatform?.morrison || []),
      ...(byPlatform?.asda || []),
      ...(byPlatform?.sainsbury || []),
    ];
    const match = (p) => matchesId(p, id);
    const sameProductFallback = (candidate) => {
      if (!candidate) return false;
      if (match(candidate)) return true;
      const candidateSku = normalizeIdentityText(candidate?.sku);
      const candidateTitle = normalizeIdentityText(candidate?.title || candidate?.product_name);
      if (currentSku && candidateSku && candidateSku === currentSku) return true;
      if (currentTitle && candidateTitle && candidateTitle === currentTitle) return true;
      return false;
    };
    const sameProductLoose = (candidate) => {
      if (!candidate) return false;
      if (sameProductFallback(candidate)) return true;
      const candidateSku = normalizeIdentityText(candidate?.sku);
      const candidateTitle = normalizeIdentityText(candidate?.title || candidate?.product_name);
      if (currentSku && candidateSku && (candidateSku.includes(currentSku) || currentSku.includes(candidateSku))) {
        return true;
      }
      if (
        currentTitle &&
        candidateTitle &&
        (candidateTitle.includes(currentTitle) || currentTitle.includes(candidateTitle))
      ) {
        return true;
      }
      return false;
    };
    // Derive current retailer key as robustly as possible so "current price" can be shown even
    // when cross-store match isn't available.
    const rawPlatform = product?.platform || product?.retailer || product?.provider || "";
    const currentPlatformKey = (() => {
      const direct =
        normalizePlatformKey(rawPlatform) ||
        (platforms.includes(String(rawPlatform || "").trim().toLowerCase())
          ? String(rawPlatform || "").trim().toLowerCase()
          : "");
      if (direct) return direct;

      // If API didn't provide a platform but we came from a specific retailer list, infer it.
      for (const k of platforms) {
        const list = byPlatform?.[k] || [];
        if (Array.isArray(list) && list.some(match)) return k;
      }
      return "";
    })();

    const rows = platforms.map((platform) => {
      const list = byPlatform?.[platform] || [];
      const found = Array.isArray(list) ? list.find(sameProductFallback) : null;
      const peer = allKnownProducts.find(
        (candidate) => normalizePlatformKey(candidate?.platform) === platform && sameProductLoose(candidate)
      );
      const raw =
        found?.price ??
        found?.unit_price ??
        found?.price_per_unit ??
        found?.pricePerUnit ??
        found?.offer_price ??
        found?.offerPrice ??
        null;
      const price = parseMoney(raw);
      const comparisonEntry =
        comparisonByPlatform && Object.prototype.hasOwnProperty.call(comparisonByPlatform, platform)
          ? comparisonByPlatform[platform]
          : null;
      const comparisonPrice =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.price
          : comparisonEntry;
      const comparisonUrl =
        comparisonEntry && typeof comparisonEntry === "object" ? comparisonEntry?.url : null;
      const comparisonRating =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.rating ?? comparisonEntry?.average_rating
          : null;
      const comparisonReviewCount =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.reviewCount ?? comparisonEntry?.review_count ?? comparisonEntry?.reviews
          : null;
      let finalPrice = comparisonPrice != null ? comparisonPrice : price;
      let finalUrl = pickVisitUrl(found);
      if (!finalUrl && comparisonUrl) finalUrl = comparisonUrl;
      if (!finalUrl && currentPlatformKey && platform === currentPlatformKey) {
        finalUrl = pickVisitUrl(product);
      }
      // If the store lists aren't loaded (or don't contain this pid), at least show the current product's retailer price.
      if (finalPrice == null && currentPlatformKey && platform === currentPlatformKey) {
        finalPrice = parseMoney(
          product?.price ?? product?.unit_price ?? product?.price_per_unit ?? product?.pricePerUnit
        );
      }
      const productLevelRating = parseRatingValue(
        product?.average_rating ?? product?.avg_rating ?? product?.rating
      );
      const productLevelReviewCount = parseReviewCountValue(
        product?.review_count ?? product?.reviews ?? product?.reviewCount ?? product?.rating_count
      );
      const ratingSource =
        found?.average_rating ??
        found?.rating ??
        peer?.average_rating ??
        peer?.rating ??
        comparisonRating ??
        (platform === currentPlatformKey ? productLevelRating : null);
      const parsedRating = parseRatingValue(ratingSource);
      const reviewCountSource =
        found?.review_count ??
        found?.reviews ??
        found?.reviewCount ??
        peer?.review_count ??
        peer?.reviews ??
        peer?.reviewCount ??
        comparisonReviewCount ??
        (platform === currentPlatformKey ? productLevelReviewCount : null);
      const parsedReviewCount = parseReviewCountValue(reviewCountSource);
      return {
        platform,
        ...labelByPlatform[platform],
        retailerBannerSrc: retailerBannerByPlatform[platform] || null,
        price: finalPrice,
        formatted: finalPrice != null ? `£${finalPrice.toFixed(2)}` : "N/A",
        visitUrl: finalUrl,
        rating: Number.isFinite(parsedRating) ? parsedRating : null,
        reviewCount: Number.isFinite(parsedReviewCount) ? parsedReviewCount : null,
      };
    });

    const prices = rows.map((r) => r.price).filter((p) => typeof p === "number" && Number.isFinite(p));
    const cheapest = prices.length ? Math.min(...prices) : null;
    const highest = prices.length ? Math.max(...prices) : null;
    const savings = cheapest != null && highest != null ? highest - cheapest : null;
    const cheapestRow = cheapest != null ? rows.find((r) => r.price === cheapest) : null;

    return {
      rows,
      cheapestRow,
      savings: savings != null && savings > 0 ? savings : null,
    };
  }, [byPlatform, topDeals, features, id, comparisonByPlatform, product]);

  const heroPrice = useMemo(() => {
    if (retailerStack.cheapestRow?.price != null) return retailerStack.cheapestRow.price;
    return product?.price ?? null;
  }, [retailerStack.cheapestRow?.price, product?.price]);

  const heroPriceLabel = retailerStack.cheapestRow?.label || product?.platform || "—";

  const images = getProductImages(product);
  const mainImage = images[0];
  const ratingAvg = product?.average_rating;
  const ratingCount = product?.review_count;
  const ratingParsed =
    ratingAvg == null
      ? null
      : Number.parseFloat(String(ratingAvg).trim().replace(",", "."));
  const reviewCountParsed = Number.parseInt(String(ratingCount), 10);
  const ratingStars =
    typeof ratingParsed === "number" && Number.isFinite(ratingParsed) ? ratingParsed.toFixed(1) : null;

  const sizeOptions = useMemo(() => {
    const raw = String(product?.packsize || "").trim();
    const list = raw
      ? raw
          .split(/[,/|]/g)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    return list.length ? list.slice(0, 6) : ["—"];
  }, [product?.packsize]);

  useEffect(() => {
    if (!sizeOptions.length) return;
    setSelectedSize((prev) => (prev && sizeOptions.includes(prev) ? prev : sizeOptions[0] || ""));
  }, [id, sizeOptions]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <BackButton />
        </div>
        {!product && (
          <div className="mt-20 rounded-2xl border border-slate-200 bg-white p-5 text-center text-sm text-[#6E7B86] shadow-sm">
            {isHydrating ? "Loading product details..." : "Product not found."}
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-[280px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product?.title || product?.sku}
                  className="max-h-64 object-contain"
                />
              ) : (
                <div className="flex h-64 w-full items-center justify-center rounded-xl bg-[#F5F7FB] text-xs text-[#6E7B86]">
                  No Image
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {images.slice(0, 4).map((img, idx) => (
                <div
                  key={idx}
                  className="flex h-16 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm"
                >
                  <img
                    src={img}
                    alt={`${product?.title || product?.sku} ${idx + 1}`}
                    className="h-12 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold text-[#24303A]">
                  {product?.title || product?.sku || "N/A"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-full bg-slate-200 px-3 py-1 font-semibold text-[#24303A]">
                    {product?.packsize || ""}
                  </span>
                  {(ratingStars != null || Number.isFinite(reviewCountParsed)) && (
                    <span className="rounded-full bg-[#7352C7]/10 px-3 py-1 font-semibold text-[#7352C7]">
                      {ratingStars != null ? `★ ${ratingStars}` : "★"}{" "}
                      {Number.isFinite(reviewCountParsed) ? `${reviewCountParsed} reviews` : ""}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-right text-xs border border-emerald-100">
                <p className="text-[11px] font-semibold text-emerald-700">
                  Cheapest at{" "}
                  <span className="text-[#24303A]">
                    {heroPriceLabel}
                  </span>
                </p>
                <p className="mt-1 text-2xl font-extrabold text-emerald-800">
                  {heroPrice != null ? `£${heroPrice.toFixed(2)}` : "£—"}
                </p>
                {retailerStack.savings != null && (
                  <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                    Save £{retailerStack.savings.toFixed(2)} vs highest
                  </p>
                )}
              </div>
            </div>

            <section className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <h2 className="text-sm font-extrabold text-[#24303A]">
                Where To Buy
                </h2>
                {retailerStack.savings != null && (
                  <div className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1 border border-emerald-100">
                    You could save up to £{retailerStack.savings.toFixed(2)}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                  {retailerStack.rows.map((row) => (
                    <RetailRow
                      key={row.platform}
                      row={row}
                      isBest={
                        retailerStack.cheapestRow != null &&
                        row.platform === retailerStack.cheapestRow.platform
                      }
                      onAddToList={handleAddToList}
                      addDisabled={addToListBusy}
                    />
                  ))}
              </div>
            </section>

            <section className="space-y-2 text-sm">
              <h2 className="text-sm font-extrabold text-[#24303A]">
                Good to know
              </h2>
              <p className="text-xs text-[#6E7B86]">
                {product?.description
                  ? product.description.slice(0, 220)
                  : "Product description is not available for this item yet."}
              </p>
            </section>

            {/* <section className="space-y-2">
              <h2 className="text-sm font-extrabold text-[#24303A]">Sizes</h2>
              <div className="space-y-2">
                {sizeOptions.map((size) => {
                  const isActive = size === selectedSize;
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setSelectedSize(size)}
                      className={[
                        "w-full rounded-2xl border p-3 text-left transition",
                        isActive
                          ? "border-[#7352C7] bg-[#7352C7]/5"
                          : "border-slate-200 bg-slate-50 hover:bg-white",
                      ].join(" ")}
                    >
                      <div className="grid gap-3 md:grid-cols-[260px_minmax(0,1fr)] md:items-center">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                            {mainImage ? (
                              <img
                                src={mainImage}
                                alt={product?.title || product?.sku}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <div className="text-[10px] font-semibold text-slate-400">
                                No image
                              </div>
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={[
                                  "rounded-full px-3 py-1 text-[11px] font-semibold",
                                  isActive
                                    ? "bg-[#7352C7] text-white"
                                    : "border border-slate-200 bg-white text-slate-700",
                                ].join(" ")}
                              >
                                {size}
                              </span>
                              <span className="text-xs font-extrabold text-slate-900">
                                {heroPrice != null ? `£${heroPrice.toFixed(2)}` : "£—"}
                              </span>
                            </div>
                            <div className="mt-1 text-[11px] font-semibold text-slate-500">
                              Compare across stores
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          {(() => {
                            const baseRows = retailerStack?.rows || [];
                            const rows = baseRows.map((r) => {
                              const p = r?.platform;
                              const overrideEntry =
                                comparisonByPlatform &&
                                p &&
                                Object.prototype.hasOwnProperty.call(comparisonByPlatform, p)
                                  ? comparisonByPlatform[p]
                                  : null;
                              const override =
                                overrideEntry && typeof overrideEntry === "object"
                                  ? overrideEntry?.price
                                  : overrideEntry;
                              const finalPrice =
                                typeof override === "number" && Number.isFinite(override)
                                  ? override
                                  : r?.price;
                              return { ...r, price: finalPrice };
                            });
                            const maxPrice = rows.reduce((max, r) => {
                              const v = r?.price;
                              return typeof v === "number" && Number.isFinite(v) ? Math.max(max, v) : max;
                            }, 0);

                            return (
                              <div className="w-[160px]">
                                <div className="flex items-end justify-between gap-1">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}`} className="flex-1">
                                      <div
                                        className="mx-auto w-full rounded-t-md"
                                        style={{
                                          backgroundColor: row?.color || "#94A3B8",
                                          height:
                                            row.price == null || maxPrice <= 0
                                              ? 10
                                              : Math.max(10, Math.round((row.price / maxPrice) * 22)),
                                          opacity: row?.price == null ? 0.25 : 1,
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-1 grid grid-cols-4 gap-1 text-[9px] font-semibold text-slate-600">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}-lbl`} className="truncate text-center">
                                      {row?.label || row?.platform}
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-0.5 grid grid-cols-4 gap-1 text-[9px] font-extrabold text-slate-900">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}-pr`} className="text-center">
                                      {row?.price != null ? `£${row.price.toFixed(2)}` : "N/A"}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section> */}
          </div>
        </div>
      </main>

      <Footer />
      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Select the list where you want to add this product.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-sm font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RetailRow({ row, isBest, onAddToList, addDisabled }) {
  return (
    <div className="grid grid-cols-[150px_1fr_auto] items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-10 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-slate-50 px-0">
          {row?.retailerBannerSrc ? (
            <img
              src={row.retailerBannerSrc}
              alt={row?.label || "Store"}
              className="w-full object-center"
              loading="lazy"
            />
          ) : (
            <span className="truncate text-[10px] font-semibold text-slate-700">
              {row?.label || "Store"}
            </span>
          )}
        </div>
        <span
          className={[
            "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold",
            isBest ? "bg-emerald-50 text-emerald-700" : "bg-transparent text-transparent",
          ].join(" ")}
        >
          Cheapest
        </span>
      </div>

      <div className="flex min-w-0 items-center justify-center">
        {(typeof row?.rating === "number" && Number.isFinite(row.rating)) ||
        Number.isFinite(row?.reviewCount) ? (
          <div className="inline-flex items-center justify-center gap-1 leading-none">
            {typeof row?.rating === "number" && Number.isFinite(row.rating) && (
              <>
                <StarRating rating={row.rating} />
                <span className="text-[10px] font-bold leading-none text-slate-800">
                  {row.rating.toFixed(1)}
                </span>
              </>
            )}
            {Number.isFinite(row?.reviewCount) && (
              <span className="text-[10px] font-semibold leading-none text-slate-600">
                {row.reviewCount} reviews
              </span>
            )}
          </div>
        ) : (
          <span className="text-[10px] font-semibold leading-none text-slate-400">No reviews</span>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 text-xs">
        <div className="flex items-center text-center">
          <span className="font-semibold text-slate-900">
            {row?.price != null ? `£${row.price.toFixed(2)}` : "N/A"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-full bg-[#7352C7] px-3 py-1 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={(e) => {
              e.stopPropagation();
              onAddToList?.(row);
            }}
            disabled={addDisabled}
          >
            Add to List
          </button>
          <button
            type="button"
            className={[
              "rounded-full px-3 py-1 text-[11px] font-semibold",
              row?.visitUrl
                ? "border border-[#7352C7] text-[#7352C7] bg-white"
                : "border border-slate-300 text-slate-400 bg-slate-100 cursor-not-allowed",
            ].join(" ")}
            onClick={(e) => {
              e.stopPropagation();
              if (!row?.visitUrl) return;
              window.open(row.visitUrl, "_blank", "noopener,noreferrer");
            }}
            disabled={!row?.visitUrl}
          >
            Visit Store
          </button>
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const clamped = Math.max(0, Math.min(5, rating || 0));
  return (
    <div
      className="inline-flex items-center gap-0.5 leading-none"
      aria-label={`Rating ${clamped.toFixed(1)} out of 5`}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fillPercent = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="relative inline-flex h-3.5 w-3.5 items-center justify-center leading-none">
            <span className="absolute inset-0 flex items-center justify-center leading-none text-slate-300">★</span>
            <span
              className="absolute inset-0 flex items-center justify-start overflow-hidden leading-none text-amber-400"
              style={{ width: `${fillPercent * 100}%` }}
            >
              ★
            </span>
          </span>
        );
      })}

    </div>
  );
}

export default ProductDetail;

